import {
  Button,
  HStack,
  Input,
  NativeSelect,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { useCallback, useEffect, useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import { Field, Modal } from '@mangovoice/mango-ui'

import type { PhoneNumberDetail } from '../api/messagingApi.types'
import type { MessagingTranslateFn } from '../types/i18n.types'
import { text } from '../types/i18n.types'

const formatPhoneDisplay = (raw: string): string => {
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return raw
}

export interface CreateMessageGroupModalProps {
  isOpen: boolean
  onClose: () => void
  pbxId: number
  onSave: (name: string, primaryPhoneNumberId: number) => void
  isSaving?: boolean
  toast: ToastLike
  /** Optional translation function for i18n; when omitted, components use fallback English */
  t?: MessagingTranslateFn
  /** Available SMS numbers for the PBX */
  availableSmsNumbers?: PhoneNumberDetail[]
}

export const CreateMessageGroupModal: React.FC<
  CreateMessageGroupModalProps
> = ({
  isOpen,
  onClose,
  pbxId,
  onSave,
  isSaving = false,
  toast,
  t,
  availableSmsNumbers = [],
}) => {
  const [name, setName] = useState('')
  const [primarySmsId, setPrimarySmsId] = useState('')

  useEffect(() => {
    if (isOpen) {
      setName('')
      setPrimarySmsId('')
    }
  }, [isOpen])

  const handleSave = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!name.trim()) {
        toast.create({
          title: text(
            t,
            'messaging.createModal.validationErrorTitle',
            'Validation Error',
          ),
          description: text(
            t,
            'messaging.createModal.validationErrorDescription',
            'Please enter a name for the message group',
          ),
          type: 'error',
          duration: 3000,
          closable: true,
        })
        return
      }
      if (!primarySmsId) {
        toast.create({
          title: text(
            t,
            'messaging.createModal.validationErrorTitle',
            'Validation Error',
          ),
          description: text(
            t,
            'messaging.createModal.primarySmsRequired',
            'Please select a primary SMS number',
          ),
          type: 'error',
          duration: 3000,
          closable: true,
        })
        return
      }
      onSave(name.trim(), parseInt(primarySmsId, 10))
    },
    [name, primarySmsId, onSave, toast, t],
  )

  return (
    <Modal
      open={isOpen}
      onOpenChange={(e) => !e.open && onClose()}
      title={text(t, 'messaging.createModal.title', 'Create Message Group')}
      maxWidth="500px"
      footer={
        <HStack gap={3} w="full">
          <Button
            borderRadius="lg"
            variant="outline"
            onClick={onClose}
            flex={1}
          >
            {text(t, 'messaging.createModal.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            form="create-message-group-form"
            borderRadius="lg"
            colorPalette="brand"
            disabled={isSaving}
            flex={1}
          >
            {isSaving
              ? text(t, 'messaging.createModal.creating', 'Creating...')
              : text(t, 'messaging.createModal.createButton', 'Create Group')}
          </Button>
        </HStack>
      }
    >
      <form id="create-message-group-form" noValidate onSubmit={handleSave}>
        <VStack gap={5} align="stretch">
          <Field.Root required>
            <Field.Label>
              {text(t, 'messaging.createModal.nameLabel', 'Group Name')}
            </Field.Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={text(
                t,
                'messaging.createModal.namePlaceholder',
                'Enter group name',
              )}
              px={3}
            />
          </Field.Root>

          <Field.Root required>
            <Field.Label>
              {text(
                t,
                'messaging.createModal.primarySmsLabel',
                'Primary SMS number',
              )}
            </Field.Label>
            <NativeSelect.Root>
              <NativeSelect.Field
                value={primarySmsId}
                onChange={(e) => setPrimarySmsId(e.target.value)}
              >
                <option value="">
                  {text(
                    t,
                    'messaging.createModal.primarySmsPlaceholder',
                    'Select a number',
                  )}
                </option>
                {availableSmsNumbers.map((num) => (
                  <option key={num.id} value={num.id}>
                    {num.name
                      ? `${num.name} - ${formatPhoneDisplay(num.number)}`
                      : formatPhoneDisplay(num.number)}
                  </option>
                ))}
              </NativeSelect.Field>
            </NativeSelect.Root>
          </Field.Root>

          <Text fontSize="sm" color="fg.muted">
            PBX ID: {pbxId}
          </Text>
        </VStack>
      </form>
    </Modal>
  )
}
